import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn.ts'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds the hover lift used for cards that are themselves interactive. */
  interactive?: boolean
  tone?: 'container' | 'low' | 'primary'
}

const TONES = {
  container: 'bg-surface-container',
  low: 'bg-surface-container-low',
  primary: 'bg-primary-container text-on-primary-container',
} as const

/** Tonal surface, 24px radius, shadow only as a hint — never as the main depth cue. */
export function Card({
  interactive = false,
  tone = 'container',
  className,
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        'rounded-lg shadow-level-1 transition-all duration-300 ease-emphasized',
        TONES[tone],
        interactive && 'hover:scale-[1.02] hover:shadow-level-2',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export interface CardHeaderProps {
  title: ReactNode
  icon?: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}

export function CardHeader({ title, icon, description, action, className }: CardHeaderProps) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-4 p-6 pb-0', className)}>
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 text-title-lg font-medium text-on-surface">
          {icon ? <span className="text-primary">{icon}</span> : null}
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-body-md text-on-surface-variant">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

export function CardBody({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('p-6', className)} {...props}>
      {children}
    </div>
  )
}
